/**
 * MissingSalesDaysPanel — cross-outlet visibility into which dates
 * are missing daily-sales rows.  Built 1-Jun-2026 for Dipali to
 * stop hunting for "who hasn't uploaded?" via ad-hoc Mongo queries.
 *
 * Data source: GET /api/daily-sales/missing-by-outlet
 *
 * Behaviour:
 *  - Renders a header tile with the total missing days across all
 *    outlets (only "daily cadence" outlets count toward the headline).
 *  - Per-outlet card lists: missing-day count, last-seen date, the
 *    actual list of missing dates (collapsible), and a "Copy WhatsApp
 *    nudge" button that puts a ready-to-send message on the clipboard
 *    so the leader can fire it off to the responsible ops person.
 *  - Date range defaults to "last 60 days, ending yesterday". Two
 *    quick buttons + custom date inputs let you scope it down.
 *  - Irregular-cadence outlets (Online/B2B, archived projects) are
 *    hidden by default and shown when a small toggle is flipped.
 */
import { useEffect, useState, useCallback, useMemo } from 'react';
import { AlertTriangle, Calendar, Clipboard, CheckCircle2, ChevronDown, ChevronUp, RefreshCw } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';

const fmtDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso + 'T00:00:00');
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
};

const fmtDateFull = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso + 'T00:00:00');
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit', weekday: 'short' });
};

const DIV_LABEL = { tdb: 'TDB', me: 'MindEscapes', cc: 'CC', lc: 'LC' };

export default function MissingSalesDaysPanel({ divisionFilter = 'tdb,me' }) {
  // Default = last 60 days, ending yesterday
  const todayIso = new Date().toISOString().slice(0, 10);
  const sixtyDaysAgo = new Date(Date.now() - 60 * 86400000).toISOString().slice(0, 10);
  const [fromDate, setFromDate] = useState(sixtyDaysAgo);
  const [toDate, setToDate] = useState(new Date(Date.now() - 86400000).toISOString().slice(0, 10));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showIrregular, setShowIrregular] = useState(false);
  const [expanded, setExpanded] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await api.get(
        `/daily-sales/missing-by-outlet?from_date=${fromDate}&to_date=${toDate}&divisions=${divisionFilter}`,
      );
      setData(r.data);
    } catch (e) {
      if (e?.response?.status === 403) {
        setData({ forbidden: true });
      } else {
        setError(e?.response?.data?.detail || e.message);
      }
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate, divisionFilter]);

  useEffect(() => { load(); }, [load]);

  const setRange = (days) => {
    const end = new Date(Date.now() - 86400000);
    const start = new Date(Date.now() - days * 86400000);
    setFromDate(start.toISOString().slice(0, 10));
    setToDate(end.toISOString().slice(0, 10));
  };

  const copyNudge = (outlet) => {
    const dates = outlet.missing_dates.map(fmtDate).join(', ');
    const msg = `Hi! Heads-up — *${outlet.outlet}* (${DIV_LABEL[outlet.division_id] || outlet.division_id}) `
      + `is missing daily sales for ${outlet.missing_days} day${outlet.missing_days === 1 ? '' : 's'}: `
      + `${dates}. Last entry was ${fmtDate(outlet.last_seen)}. `
      + `Please upload via /division/${outlet.division_id} → Daily Sales tab. Thanks!`;
    navigator.clipboard.writeText(msg).then(
      () => toast.success('WhatsApp nudge copied — paste into the team chat'),
      () => toast.error('Could not copy — select & copy manually'),
    );
  };

  const visibleOutlets = useMemo(() => {
    if (!data?.outlets) return [];
    return data.outlets.filter((o) => o.missing_days > 0
      && (showIrregular || o.cadence === 'daily'));
  }, [data, showIrregular]);

  if (loading && !data) {
    return (
      <div className="rounded-lg border border-dashed px-4 py-3 text-xs text-[#9CA3AF]" style={{ borderColor: '#E5E1DC' }}
        data-testid="missing-sales-loading">
        Loading missing-days audit…
      </div>
    );
  }
  if (data?.forbidden) return null;
  if (error) {
    return (
      <div className="rounded-lg px-4 py-3 text-xs"
        style={{ backgroundColor: '#FCEBEB', color: '#A32D2D', border: '1px solid #E8B4B4' }}>
        Missing-days audit failed: {error}
      </div>
    );
  }
  if (!data) return null;

  const total = data.summary?.total_missing_days || 0;
  const isClean = total === 0;
  const colors = isClean
    ? { bg: '#E8F7F4', fg: '#0F766E', border: '#BAE7DC', chip: '#0F766E' }
    : { bg: '#FEF6E4', fg: '#92400E', border: '#FACC15', chip: '#92400E' };

  return (
    <div className="rounded-lg" style={{ backgroundColor: colors.bg, border: `1.5px solid ${colors.border}` }}
      data-testid="missing-sales-panel">

      {/* Header */}
      <div className="flex items-start justify-between gap-3 px-4 py-3 border-b flex-wrap" style={{ borderColor: colors.border }}>
        <div className="flex items-start gap-2">
          {isClean
            ? <CheckCircle2 size={18} className="mt-0.5" style={{ color: colors.fg }} />
            : <AlertTriangle size={18} className="mt-0.5" style={{ color: colors.fg }} />}
          <div>
            <p className="text-sm font-semibold" style={{ color: colors.fg }}>
              {isClean
                ? 'All daily-reporting outlets are caught up'
                : `${total} missing day${total === 1 ? '' : 's'} across ${visibleOutlets.length} outlet${visibleOutlets.length === 1 ? '' : 's'}`}
            </p>
            <p className="text-[11px] mt-0.5" style={{ color: colors.fg, opacity: 0.85 }}>
              <Calendar size={10} className="inline mb-0.5" /> {fmtDateFull(data.window.from)} → {fmtDateFull(data.window.to)} · {data.window.days} day window · scanning {(data.divisions_scanned || []).map((d) => DIV_LABEL[d] || d).join(' + ')}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1.5 flex-wrap">
          <button onClick={() => setRange(7)}
            className="text-[10px] px-2 py-1 rounded border hover:bg-white/40"
            style={{ borderColor: colors.border, color: colors.fg }}
            data-testid="missing-sales-range-7">Last 7d</button>
          <button onClick={() => setRange(30)}
            className="text-[10px] px-2 py-1 rounded border hover:bg-white/40"
            style={{ borderColor: colors.border, color: colors.fg }}
            data-testid="missing-sales-range-30">Last 30d</button>
          <button onClick={() => setRange(60)}
            className="text-[10px] px-2 py-1 rounded border hover:bg-white/40"
            style={{ borderColor: colors.border, color: colors.fg }}
            data-testid="missing-sales-range-60">Last 60d</button>
          <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)}
            max={toDate}
            className="text-[10px] px-1.5 py-1 rounded border bg-white/60" style={{ borderColor: colors.border, color: colors.fg }}
            data-testid="missing-sales-from-input" />
          <span className="text-[10px]" style={{ color: colors.fg }}>→</span>
          <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)}
            min={fromDate} max={todayIso}
            className="text-[10px] px-1.5 py-1 rounded border bg-white/60" style={{ borderColor: colors.border, color: colors.fg }}
            data-testid="missing-sales-to-input" />
          <button onClick={load} disabled={loading}
            className="text-[10px] px-2 py-1 rounded border hover:bg-white/40 flex items-center gap-1 disabled:opacity-50"
            style={{ borderColor: colors.border, color: colors.fg }}
            data-testid="missing-sales-refresh">
            <RefreshCw size={10} className={loading ? 'animate-spin' : ''} /> Refresh
          </button>
        </div>
      </div>

      {/* Outlet cards */}
      {visibleOutlets.length === 0 && !isClean && (
        <div className="px-4 py-3 text-xs italic" style={{ color: colors.fg, opacity: 0.85 }}>
          No outlets with missing daily-cadence days. {data.summary?.irregular_outlets > 0
            && `(${data.summary.irregular_outlets} irregular-cadence outlet${data.summary.irregular_outlets === 1 ? '' : 's'} hidden — toggle below to show.)`}
        </div>
      )}

      {visibleOutlets.length > 0 && (
        <div className="px-3 py-3 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
          {visibleOutlets.map((o) => {
            const key = `${o.division_id}::${o.outlet}`;
            const isExpanded = !!expanded[key];
            return (
              <div key={key} className="bg-white rounded-md shadow-sm border p-3"
                style={{ borderColor: o.cadence === 'irregular' ? '#D1D5DB' : '#FACC15' }}
                data-testid={`missing-sales-outlet-${o.division_id}-${o.outlet.replace(/\s+/g, '-')}`}>
                <div className="flex items-start justify-between gap-1.5 mb-2">
                  <div className="min-w-0">
                    <p className="text-[11px] uppercase tracking-wider font-bold"
                       style={{ color: o.cadence === 'irregular' ? '#9CA3AF' : '#92400E' }}>
                      {DIV_LABEL[o.division_id] || o.division_id}
                      {o.cadence === 'irregular' && ' · irregular'}
                    </p>
                    <p className="text-sm font-semibold truncate text-[#1B2A4A]" title={o.outlet}>
                      {o.outlet}
                    </p>
                  </div>
                  <span className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-full whitespace-nowrap"
                    style={{ backgroundColor: '#FEF6E4', color: '#92400E' }}>
                    {o.missing_days} missing
                  </span>
                </div>
                <div className="text-[11px] text-[#6B7280] mb-2">
                  Last entry · <strong className="text-[#1B2A4A]">{fmtDateFull(o.last_seen)}</strong>
                </div>

                {/* Missing dates list (collapsed by default if >6) */}
                <button onClick={() => setExpanded({ ...expanded, [key]: !isExpanded })}
                  className="text-[10px] text-[#2A7B88] hover:underline flex items-center gap-0.5 mb-1.5"
                  data-testid={`missing-sales-toggle-${o.division_id}-${o.outlet.replace(/\s+/g, '-')}`}>
                  {isExpanded ? <ChevronUp size={10} /> : <ChevronDown size={10} />}
                  {isExpanded ? 'Hide dates' : `Show all ${o.missing_days} dates`}
                </button>
                {isExpanded && (
                  <div className="flex flex-wrap gap-1 mb-2 max-h-32 overflow-y-auto">
                    {o.missing_dates.map((d) => (
                      <span key={d} className="text-[10px] px-1.5 py-0.5 rounded font-mono"
                        style={{ backgroundColor: '#FEF6E4', color: '#92400E' }}>
                        {fmtDate(d)}
                      </span>
                    ))}
                  </div>
                )}

                <button onClick={() => copyNudge(o)}
                  className="w-full flex items-center justify-center gap-1 px-2 py-1.5 rounded text-[10px] font-medium border hover:bg-[#F7F5F2]"
                  style={{ borderColor: '#E5E1DC', color: '#2A7B88' }}
                  data-testid={`missing-sales-copy-${o.division_id}-${o.outlet.replace(/\s+/g, '-')}`}>
                  <Clipboard size={10} /> Copy WhatsApp nudge
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* Footer */}
      <div className="border-t px-4 py-2 flex items-center justify-between text-[11px]"
        style={{ borderColor: colors.border, color: colors.fg }}>
        <span>
          {data.summary?.healthy_outlets || 0} of {data.summary?.daily_outlets || 0} daily outlets caught up
        </span>
        {data.summary?.irregular_outlets > 0 && (
          <label className="flex items-center gap-1.5 cursor-pointer">
            <input type="checkbox" checked={showIrregular}
              onChange={(e) => setShowIrregular(e.target.checked)}
              data-testid="missing-sales-show-irregular" />
            Show {data.summary.irregular_outlets} irregular-cadence outlet{data.summary.irregular_outlets === 1 ? '' : 's'}
          </label>
        )}
      </div>
    </div>
  );
}
